import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import { getBikes, getSelfGuidedRoutes } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Home() {
  const [bikes, setBikes] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [routeCount, setRouteCount] = useState(3);

  useEffect(() => {
    getBikes().then((d) => { setFleet(d); setBikes(d.slice(0, 3)); }).catch(() => {});
    getSelfGuidedRoutes().then((r) => setRouteCount(r.length)).catch(() => {});
  }, []);
  const minPrice = fleet.length ? Math.min(...fleet.map((b) => b.price_per_hour)) : 20;

  return (
    <div className="bg-[#F5F5F0]">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 pt-16 md:pt-24 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="md:col-span-6 flex flex-col justify-center"
          >
            <p className="overline text-[#001A4D] mb-6">Bike Hire · Self-Guided Adventures</p>
            <h1 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight text-[#0A0A0A]">
              Ride further<br />than the map.
            </h1>
            <p className="mt-8 text-lg text-[#0A0A0A]/70 max-w-md leading-relaxed">
              Hire a bike by the hour and explore Whangamatā
              your way — or unlock a self-guided adventure with stories at every stop.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/book" data-testid="hero-hire-btn">
                <Button className="rounded-full h-12 px-8 bg-[#001A4D] hover:bg-[#00287A] text-white text-base">
                  Book a bike <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/self-guided" data-testid="hero-adventures-btn">
                <Button
                  variant="outline"
                  className="rounded-full h-12 px-8 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-base"
                >
                  Self-guided adventures
                </Button>
              </Link>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9 }}
            className="md:col-span-6 relative"
          >
            <div className="relative h-[380px] md:h-[560px] rounded-2xl overflow-hidden">
              <img
                src="/brand/photos/p1.jpg"
                alt="Cycling on the coast"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-6 left-6 text-white">
                <p className="overline opacity-80">Popular</p>
                <p className="font-serif text-3xl">Coastal Cruiser Adventure</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-y border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-black/10">
          {[
            [fleet.length ? String(fleet.reduce((s, b) => s + b.total_qty, 0)) : "—", "Bikes in fleet"],
            [fleet.length ? String(fleet.length) : "—", "Bike types"],
            [String(routeCount), "Self-guided routes"],
            [`$${minPrice}`, "From, per hour"],
          ].map(([n, l]) => (
            <div key={l} className="py-8 px-4 text-center">
              <p className="font-serif text-4xl text-[#001A4D]">{n}</p>
              <p className="overline text-[#0A0A0A]/50 mt-2">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured bikes */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="overline text-[#001A4D] mb-3">The Fleet</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0A0A0A]">Hire by the hour</h2>
          </div>
          <Link to="/bikes" className="hidden md:flex items-center gap-2 text-[#001A4D] font-medium hover:gap-3 transition-all" data-testid="view-all-bikes">
            View all bikes <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {bikes.map((b, i) => (
            <motion.div
              key={b.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group"
            >
              <div className="h-64 rounded-xl overflow-hidden bg-neutral-200 mb-4">
                <img src={b.image} alt={b.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-2xl text-[#0A0A0A]">{b.name}</h3>
                <p className="font-mono text-[#001A4D] font-medium">${b.price_per_hour}<span className="text-xs text-[#0A0A0A]/40">/hr</span></p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Self-guided promo */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center bg-white rounded-3xl border border-black/5 overflow-hidden">
          <div className="md:col-span-6 p-10 md:p-14">
            <p className="overline text-[#001A4D] mb-4">Self-Guided Adventures</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0A0A0A] leading-tight">
              Explore at your own pace
            </h2>
            <p className="mt-5 text-[#0A0A0A]/70 leading-relaxed max-w-md">
              Hire a bike, grab a route card, enter your code, and follow the map.
              At every stop, tap play to hear the story of the place — spoken aloud.
              Three routes, from a gentle Park Hop to the full Whanga Adventure.
            </p>
            <Link to="/self-guided" data-testid="home-self-guided-btn">
              <Button className="mt-8 rounded-full h-12 px-8 bg-[#0A0A0A] hover:bg-[#001A4D] text-white">
                Start an adventure <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="md:col-span-6 h-72 md:h-96">
            <img
              src="/brand/photos/p5.jpg"
              alt="Coastal cycling"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
